import { useEffect, useState } from "react";
import { X, Trash2, Plus } from "lucide-react";
import { useLang } from "@/lib/adminLang";
import { CATEGORIES, BEHAVIORS, STATUSES, SCEN, nok, miniInput } from "./budsjettUtils";
import { PlanEditor } from "./PlanEditor";

const Section = ({ label, children }) => (
  <div className="mt-5">
    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400 mb-2">{label}</p>
    {children}
  </div>
);

const UNIT_LABEL = {
  per_physical: "Cost per physical attendee",
  per_digital: "Cost per digital attendee",
  per_dinner: "Cost per dinner guest",
  stepped: "Cost per tier",
  pct_revenue: "% of total revenue",
};

export default function LineDrawer({ line, kind, members, onSave, onDelete, onClose }) {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: line?.name || "",
    supplier: line?.supplier || "",
    category: line?.category || (kind === "revenue_other" ? "other_revenue" : "other"),
    ownerId: line?.ownerId || "",
    behavior: line?.behavior || "fixed",
    budget: line?.budget ?? 0,
    committed: line?.committed ?? 0,
    forecast: line?.forecast ?? 0,
    unitCost: line?.unitCost ?? 0,
    unitSize: line?.unitSize ?? 500,
    actual: line?.actual ?? 0,
    quoteAmount: line?.quoteAmount ?? "",
    components: line?.components || [],
    vatPct: line?.vatPct ?? 25,
    status: line?.status || "planned",
    paymentPlan: line?.paymentPlan || [],
    scenarioOverrides: line?.scenarioOverrides || {},
    notes: line?.notes || "",
  });
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const compSum = form.components.reduce((a, c) => a + (Number(c.amount) || 0), 0);
  const forecastAuto = form.behavior !== "fixed" || form.components.length > 0;

  const setComp = (i, field, v) =>
    set("components", form.components.map((c, j) => (j === i ? { ...c, [field]: v } : c)));

  const save = async () => {
    if (!form.name.trim() || saving) return;
    setSaving(true);
    try {
      const ov = {};
      for (const sc of SCEN) {
        const v = form.scenarioOverrides[sc.k];
        if (v !== "" && v != null) ov[sc.k] = Number(v);
      }
      await onSave({
        ...form,
        budget: Number(form.budget) || 0,
        committed: Number(form.committed) || 0,
        forecast: Number(form.forecast) || 0,
        unitCost: Number(form.unitCost) || 0,
        unitSize: Number(form.unitSize) || 500,
        actual: Number(form.actual) || 0,
        quoteAmount: form.quoteAmount === "" ? null : Number(form.quoteAmount),
        components: form.components
          .filter((c) => String(c.label || "").trim())
          .map((c) => ({ label: c.label, amount: Number(c.amount) || 0 })),
        vatPct: Number(form.vatPct) || 0,
        ownerId: form.ownerId || null,
        scenarioOverrides: ov,
        note: note.trim() || null,
      });
    } finally {
      setSaving(false);
    }
  };

  const activity = line ? [...(line.activity || [])].reverse() : [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="line-drawer">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onClose} />
      <aside className="sak-drawer relative w-full max-w-[560px] h-full bg-white shadow-2xl border-l border-[#E7E4DB] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 shrink-0">
          <h2 className="text-[15px] font-semibold">
            {line ? t("Edit line") : kind === "revenue_other" ? t("New revenue line") : t("New cost line")}
          </h2>
          <div className="flex items-center gap-1">
            {onDelete && (
              <button type="button" onClick={onDelete} className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-testid="line-delete-btn">
                <Trash2 size={15} />
              </button>
            )}
            <button type="button" onClick={onClose} className="p-1.5 text-neutral-400 hover:text-neutral-800 hover:bg-neutral-100 rounded-md transition-colors" data-testid="line-close-btn">
              <X size={17} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="grid grid-cols-2 gap-3">
            <label className="adm-label col-span-2">
              {t("Name")}
              <input value={form.name} onChange={(e) => set("name", e.target.value)} className="adm-input" data-testid="line-name-input" />
            </label>
            <label className="adm-label">
              {t("Supplier")}
              <input value={form.supplier} onChange={(e) => set("supplier", e.target.value)} className="adm-input" data-testid="line-supplier-input" />
            </label>
            {kind === "cost" && (
              <label className="adm-label">
                {t("Category")}
                <select value={form.category} onChange={(e) => set("category", e.target.value)} className="adm-input" data-testid="line-category-select">
                  {CATEGORIES.map((c) => (
                    <option key={c.k} value={c.k}>{t(c.label)}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="adm-label">
              {t("Owner")}
              <select value={form.ownerId} onChange={(e) => set("ownerId", e.target.value)} className="adm-input" data-testid="line-owner-select">
                <option value="">{t("None")}</option>
                {(members || []).map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </label>
            <label className="adm-label">
              {t("Status")}
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className="adm-input" data-testid="line-status-select">
                {STATUSES.map((st) => (
                  <option key={st.k} value={st.k}>{t(st.label)}</option>
                ))}
              </select>
            </label>
            <label className="adm-label">
              {t("Quote amount (ex VAT)")}
              <input
                type="number"
                placeholder="—"
                value={form.quoteAmount}
                onChange={(e) => set("quoteAmount", e.target.value)}
                className="adm-input"
                data-testid="line-quote-input"
              />
            </label>
          </div>
          <p className="text-[10.5px] text-neutral-400 mt-1.5">
            {t("Status is a document layer (quote → contract → invoice) — it never changes the numbers by itself.")}
          </p>

          {kind === "cost" && (
            <Section label={t("Cost driver")}>
              <div className="grid grid-cols-3 gap-3">
                <label className="adm-label col-span-2 sm:col-span-1">
                  {t("Type")}
                  <select value={form.behavior} onChange={(e) => set("behavior", e.target.value)} className="adm-input" data-testid="line-behavior-select">
                    {BEHAVIORS.map((b) => (
                      <option key={b.k} value={b.k}>{t(b.label)}</option>
                    ))}
                  </select>
                </label>
                {form.behavior !== "fixed" && (
                  <label className="adm-label col-span-2">
                    {t(UNIT_LABEL[form.behavior] || "Unit cost")}
                    <input type="number" value={form.unitCost} onChange={(e) => set("unitCost", e.target.value)} className="adm-input" data-testid="line-unitcost-input" />
                  </label>
                )}
                {form.behavior === "stepped" && (
                  <label className="adm-label">
                    {t("Per # attendees")}
                    <input type="number" value={form.unitSize} onChange={(e) => set("unitSize", e.target.value)} className="adm-input" data-testid="line-unitsize-input" />
                  </label>
                )}
              </div>
              {form.behavior !== "fixed" && (
                <p className="text-[10.5px] text-neutral-400 mt-1.5">{t("Forecast is computed automatically from each scenario’s drivers.")}</p>
              )}
            </Section>
          )}

          <Section label={t("Numbers (NOK ex VAT)")}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <label className="adm-label">
                {t("Budget")}
                <input type="number" value={form.budget} onChange={(e) => set("budget", e.target.value)} className="adm-input" data-testid="line-budget-input" />
              </label>
              <label className="adm-label">
                {t("Committed")}
                <input type="number" value={form.committed} onChange={(e) => set("committed", e.target.value)} className="adm-input" data-testid="line-committed-input" />
              </label>
              <label className="adm-label">
                {t("Forecast")}
                <input
                  type="number"
                  value={form.forecast}
                  onChange={(e) => set("forecast", e.target.value)}
                  disabled={forecastAuto}
                  className="adm-input disabled:opacity-40"
                  data-testid="line-forecast-input"
                />
              </label>
              <label className="adm-label">
                {t("Actual")}
                <input type="number" value={form.actual} onChange={(e) => set("actual", e.target.value)} className="adm-input" data-testid="line-actual-input" />
              </label>
            </div>
            <label className="adm-label mt-3 w-28">
              {t("VAT %")}
              <input type="number" value={form.vatPct} onChange={(e) => set("vatPct", e.target.value)} className="adm-input" data-testid="line-vat-input" />
            </label>
          </Section>

          <Section label={t("Forecast build-up (optional — forecast = sum of components)")}>
            <div data-testid="line-components">
              {form.components.map((c, i) => (
                <div key={i} className="flex items-center gap-2 mb-1.5">
                  <input
                    value={c.label || ""}
                    onChange={(e) => setComp(i, "label", e.target.value)}
                    className="flex-1 text-[12.5px] border border-[#DED9CD] rounded-lg px-2 py-1 bg-white focus:outline-none focus:border-neutral-900"
                    data-testid={`line-comp-label-${i}`}
                  />
                  <input
                    type="number"
                    value={c.amount ?? ""}
                    onChange={(e) => setComp(i, "amount", e.target.value)}
                    className={miniInput}
                    data-testid={`line-comp-amount-${i}`}
                  />
                  <button
                    type="button"
                    onClick={() => set("components", form.components.filter((_, j) => j !== i))}
                    className="text-neutral-300 hover:text-red-500 transition-colors"
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
              <div className="flex items-center gap-3 mt-1">
                <button
                  type="button"
                  onClick={() => set("components", [...form.components, { label: "", amount: 0 }])}
                  className="inline-flex items-center gap-1 text-[11.5px] text-neutral-500 hover:text-neutral-900 transition-colors"
                  data-testid="line-comp-add-btn"
                >
                  <Plus size={12} /> {t("Add component")}
                </button>
                {form.components.length > 0 && (
                  <span className="text-[11px] tabular-nums text-neutral-500">= {nok(compSum)}</span>
                )}
              </div>
            </div>
          </Section>

          <Section label={t("Override forecast per scenario (optional)")}>
            <div className="grid grid-cols-3 gap-3">
              {SCEN.map((sc) => (
                <label key={sc.k} className="adm-label">
                  {sc.label}
                  <input
                    type="number"
                    placeholder={t("Auto")}
                    value={form.scenarioOverrides[sc.k] ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        scenarioOverrides: { ...f.scenarioOverrides, [sc.k]: e.target.value === "" ? null : e.target.value },
                      }))
                    }
                    className="adm-input"
                    data-testid={`line-override-${sc.k}`}
                  />
                </label>
              ))}
            </div>
          </Section>

          <Section label={t("Payment plan (empty = event month · cash flow uses incl. VAT)")}>
            <PlanEditor plan={form.paymentPlan} onChange={(p) => set("paymentPlan", p)} testid="line-plan-editor" />
          </Section>

          <Section label={t("Notes")}>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="adm-input" data-testid="line-notes-input" />
          </Section>

          {line && (
            <Section label={t("Change note (logged in the change log)")}>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="adm-input"
                data-testid="line-changenote-input"
              />
            </Section>
          )}

          {activity.length > 0 && (
            <Section label={t("Change log")}>
              <div className="space-y-1" data-testid="line-changelog">
                {activity.slice(0, 10).map((a, i) => (
                  <p key={i} className="text-[11px] text-neutral-400 leading-relaxed">
                    <span className="text-neutral-600 font-medium">{a.actor}</span> · {a.text}
                    <span className="ml-1.5 text-neutral-300">
                      {new Date(a.at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                    </span>
                  </p>
                ))}
              </div>
            </Section>
          )}
        </div>

        <div className="shrink-0 px-6 py-3 border-t border-neutral-100 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="adm-btn-ghost">{t("Cancel")}</button>
          <button type="button" onClick={save} disabled={!form.name.trim() || saving} className="adm-btn" data-testid="line-save-btn">
            {saving ? t("Saving …") : t("Save line")}
          </button>
        </div>
      </aside>
    </div>
  );
}
